import type {
    NotificationsListRequest,
    NotificationsListResponse,
    UnreadCountResponse,
    NotificationActionResponse,
} from '@/types/notifications';

const API_BASE_URL = 'https://dpscontrol.ru';

// Получаем initData из Telegram WebApp
const getTelegramInitData = (): string => {
    if (window.Telegram?.WebApp?.initData) {
        return window.Telegram.WebApp.initData;
    }

    // Для разработки
    if (import.meta.env.VITE_DEVELOP_MODE) {
        return import.meta.env.VITE_DEVELOP_MODE;
    }

    return '';
};

// Базовая функция для API запросов (согласно новой спецификации)
const apiRequest = async <T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> => {
    const initData = getTelegramInitData();
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'X-Telegram-Init-Data': initData, // Изменен заголовок согласно API
            ...options.headers,
        },
    });

    if (!response.ok) {
        try {
            const errorData = await response.json();
            throw new Error(errorData.error || `API Error ${response.status}`);
        } catch {
            throw new Error(`API Error ${response.status}`);
        }
    }

    const data = await response.json();

    // Проверка на success: false в ответе
    if ('success' in data && data.success === false) {
        throw new Error(data.error || 'API request failed');
    }

    return data;
};

export const notificationsApi = {
    /**
     * Получить список уведомлений с фильтрами и пагинацией
     */
    getNotifications: async (
        params: NotificationsListRequest = {}
    ): Promise<NotificationsListResponse> => {
        const queryParams = new URLSearchParams();

        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());
        if (params.is_read !== undefined) queryParams.append('is_read', params.is_read.toString());
        if (params.category) queryParams.append('category', params.category);
        if (params.type) queryParams.append('type', params.type);

        const query = queryParams.toString();
        const endpoint = `/api/v1/notifications${query ? `?${query}` : ''}`;

        console.log('📬 Загрузка уведомлений:', endpoint);

        try {
            const response = await apiRequest<NotificationsListResponse>(endpoint);
            console.log('✅ Уведомления загружены:', response);
            return response;
        } catch (error) {
            console.error('❌ Ошибка загрузки уведомлений:', error);
            throw error;
        }
    },

    /**
     * Получить количество непрочитанных уведомлений
     */
    getUnreadCount: async (): Promise<number> => {
        console.log('🔔 Загрузка счетчика непрочитанных');

        try {
            const response = await apiRequest<UnreadCountResponse>(
                '/api/v1/notifications/unread-count'
            );
            console.log('✅ Счетчик загружен:', response.count);
            return response.count;
        } catch (error) {
            console.error('❌ Ошибка загрузки счетчика:', error);
            throw error;
        }
    },

    /**
     * Отметить уведомление как прочитанное
     */
    markAsRead: async (notificationId: number): Promise<NotificationActionResponse> => {
        console.log('👁️ Отметка уведомления как прочитанного:', notificationId);

        try {
            const response = await apiRequest<NotificationActionResponse>(
                `/api/v1/notifications/${notificationId}/read`,
                { method: 'POST' }
            );
            console.log('✅ Уведомление отмечено как прочитанное');
            return response;
        } catch (error) {
            console.error('❌ Ошибка отметки уведомления:', error);
            throw error;
        }
    },

    /**
     * Отметить все уведомления как прочитанные
     */
    markAllAsRead: async (): Promise<NotificationActionResponse> => {
        console.log('👁️ Отметка всех уведомлений как прочитанных');

        try {
            const response = await apiRequest<NotificationActionResponse>(
                '/api/v1/notifications/read-all',
                { method: 'POST' }
            );
            console.log('✅ Все уведомления отмечены как прочитанные');
            return response;
        } catch (error) {
            console.error('❌ Ошибка отметки всех уведомлений:', error);
            throw error;
        }
    },

    /**
     * Удалить уведомление
     */
    deleteNotification: async (notificationId: number): Promise<NotificationActionResponse> => {
        console.log('🗑️ Удаление уведомления:', notificationId);

        try {
            const response = await apiRequest<NotificationActionResponse>(
                `/api/v1/notifications/${notificationId}`,
                { method: 'DELETE' }
            );
            console.log('✅ Уведомление удалено');
            return response;
        } catch (error) {
            console.error('❌ Ошибка удаления уведомления:', error);
            throw error;
        }
    },
};
